import * as Dialog from '@radix-ui/react-dialog';
import { useState, useEffect } from 'react';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { publishQueries } from '@/entities/sets/publish';
import { PublishedQrPopup } from './published-qr-popup';
import { showToast } from '@/shared/utils/toast';
import { useRouter } from '@tanstack/react-router';
import { useLoginStore, usePersistStore } from '@/shared/lib/store';
import { ClassSharePopup } from './class-share-popup';
import useToggle from '@/shared/hooks/useVisible';
import { getSuffix } from '@/shared/utils/common';
import { VivaModalForm } from '@/shared/ui';
import { useCreateShortUrl } from '@/shared/hooks/useCreateShortUrl';
import { encryptDataDeterministic } from '@/shared/utils/publish';

export const PublishedCompletePopup = ({
    publishGrpId,
    timerWhether,
    hours,
    minutes,
}: {
    publishGrpId: number;
    timerWhether: boolean;
    hours: number;
    minutes: number;
}) => {
    const router = useRouter();
    const { showModal, closeModal } = useModalStore();
    const { sUserId, sMyClassMemberList, sToken } = usePersistStore();
    const { loginInfo } = useLoginStore();
    const [selectExam, setSelectExam] = useState<number>();
    const [selectExamIndex, setSelectExamIndex] = useState<number>(0); // 다중 출제 시 인덱스
    const [selectExamOpen, setSelectExamOpen] = useToggle();
    const [selectClass, setSelectClass] = useState<number>(); // 다중 출제 시 인덱스
    const [selectClassIndex, setSelectClassIndex] = useState<number>(0); // 다중 출제 시 인덱스
    const [selectClassOpen, setSelectClassOpen] = useToggle();

    const SECRET_KEY = import.meta.env.VITE_TTUK_URL_DATA_KEY;

    /** 모달 JSX. 원하는 스타일과 내용을 추가/커스터마이징 */
    const { data } = publishQueries.publish.getIntegPubCompleteList(
        {
            publishGrpId: publishGrpId,
        },
        !!publishGrpId
    );

    const isMultiPublish = data && data?.examList?.length > 1;
    const isSingePubMultiClass =
        data && data.examList?.length === 1 && data?.examList[0].publishList?.length > 1;
    const isMultiClass =
        data && data.examList?.some((item) => item.publishList && item.publishList.length > 1);

    useEffect(() => {
        if (data) {
            if (!selectExam) {
                setSelectExam(data?.examList[0]?.examId);
                setSelectClass(data?.examList[0]?.publishList[0]?.publishId);
            }
            if (selectExam) {
                const index = data?.examList.findIndex((v) => v?.examId === selectExam);
                setSelectExamIndex(index);
                if (selectClass) setSelectClass(undefined);
            }
            getUrlToken();
        }
    }, [data, selectExam]);

    useEffect(() => {
        if (data) {
            if (!selectClass) {
                setSelectClass(data?.examList[selectExamIndex]?.publishList[0]?.publishId);
            }
            if (selectClass) {
                const index = data?.examList[selectExamIndex]?.publishList.findIndex(
                    (v) => v?.publishId === selectClass
                );
                setSelectClassIndex(index);
            }
        }
    }, [selectClass]);

    const findClaName = (claId: string) => {
        const classSeq = claId.split('-')[4];
        const className = sMyClassMemberList?.find(
            (v) => v.classInfo.classSeq.toString() === classSeq
        )?.classInfo.clsName;
        return className;
    };

    const paramData = {
        taskId: data?.examList[selectExamIndex]?.publishList[selectClassIndex]?.publishId,
        claId: data?.examList[selectExamIndex]?.publishList[selectClassIndex]?.claId,
        tcUserId: loginInfo?.memberId,
        trgtId: data?.examList[selectExamIndex]?.publishList[selectClassIndex]?.trgtId,
        publishType: data?.publishType,
    };

    const { data: detailData } = publishQueries.detail.getPublishDetail(
        {
            publishId: data?.examList[selectExamIndex]?.publishList[selectClassIndex].publishId!,
            wrterId: sUserId,
        },
        !!data?.examList[selectExamIndex]?.publishList[selectClassIndex].publishId && !!sUserId,
        false
    );

    // 클래스 발행
    const onStartEval = () => {
        const url =
            import.meta.env.VITE_VDT_DOMAIN +
            `?type=13&claId=${detailData?.publishInfoDetail?.claId}&trgtId=${detailData?.publishInfoDetail?.trgtId}&publishId=${detailData?.publishInfoDetail?.publishId}`;

        // const url = `https://${import.meta.env.MODE === 'development' || import.meta.env.VITE_DEPLOY === 'dev' ? 'dev-' : ''}vivaclass.vivasam.com/ourclass/homework/list?classSeq=${
        //     data?.examList[selectExamIndex]?.publishList[selectClassIndex]?.claId.split('-')[4]
        // }`;
        window.open(url, '_blank');
    };

    // URL 발행
    const encryptedString = encryptDataDeterministic(paramData);
    const shareUrl = `${window.location.origin}/eval/$${encryptedString}`;

    const { shortUrl, isLoading, error, getUrlToken } = useCreateShortUrl({
        shareUrl: shareUrl,
        startDate: data?.pdPublishStDt ?? '',
        endDate: data?.pdPublishEdDt ?? '',
    });

    const onSendData = () => {
        router.navigate({ to: `/eval/$${encryptedString}` });
    };

    const onQrOpen = () => {
        showModal({
            id: 'publish-qr-popup',
            isCustom: true,
            initContents: () => (
                <PublishedQrPopup
                    publishType={data?.publishType}
                    title={data?.examList[selectExamIndex].examNm}
                    shareUrl={shortUrl ?? shareUrl}
                />
            ),
        });
    };

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(shortUrl ?? shareUrl);
            showToast('URL 복사 완료', 2000);
        } catch (error) {
            console.error('복사 오류:', error);
        }
    };

    const onShareGoogle = () => {
        window.open('https://classroom.google.com/share?url=' + (shortUrl ?? shareUrl), '_blank');
    };

    const onShareViva = () => {
        if (
            sMyClassMemberList &&
            sMyClassMemberList?.filter((v) => v.classInfo.classState === '1').length > 0
        )
            showModal({
                isCustom: true,
                id: 'viva-share-class',
                initContents: () => (
                    <ClassSharePopup
                        shareUrl={shortUrl ?? shareUrl}
                        title={data?.examList[selectExamIndex].examNm ?? ''}
                        startDate={data?.pdPublishStDt ?? ''}
                        endDate={data?.pdPublishEdDt ?? ''}
                        timerWhether={timerWhether}
                        hours={hours}
                        minutes={minutes}
                    />
                ),
            });
        else onCreateClass();
    };

    const onCreateClass = () => {
        showModal({
            isCustom: true,
            id: 'viva-create-class',
            initContents: () => (
                <VivaModalForm
                    onOk={() => {
                        // closeModal('viva-create-class');
                        window.open(
                            `https://${import.meta.env.MODE === 'development' || import.meta.env.VITE_DEPLOY === 'dev' ? 'dev-' : ''}vivaclass.vivasam.com/main`,
                            '_blank'
                        );
                    }}
                    classId="noneadminViva"
                    btnClass="btnApply"
                    popupId="viva-create-class"
                    title={
                        <p className="tit">
                            우리반 <strong>비바클래스</strong>를 만드세요.
                        </p>
                    }
                    description={
                        <>
                            <strong>
                                우리반 비바클래스를 만들고,
                                <br />
                                비바샘에서 제공하는 자료들을
                                <br />
                                학생들에게 공유하세요.
                            </strong>
                        </>
                    }
                    btnText="지금 바로 만들기"
                />
            ),
        });
    };

    const onConfirm = () => {
        closeModal('publish-complete');
        if (data?.publishType === 1 && selectClass) {
            if (isSingePubMultiClass) return;
            if (!isSingePubMultiClass) onStartEval();
        }
        if (data?.publishType === 2) {
            // onSendData();
        }
    };

    return (
        <div className="pop_contents publish_option_popcont complite_popcont_class">
            <div className="set-file-content content-type2">
                <div className="form-row">
                    <div className="form-td">
                        <div className="cont_wrap">
                            {data?.publishType !== 3 && (
                                <div
                                    className={`cont_wrap_inner flex_c search-form report_data ${!isMultiPublish && data?.publishType === 2 && 'bor_b_none'} `}
                                >
                                    <div className="popup_cont_title">
                                        <p className="txtCenter">
                                            <strong>
                                                <span className="txt_point">
                                                    {data?.examList[0]?.examNm}
                                                    {data &&
                                                        data?.examList?.length > 1 &&
                                                        '외 ' + (data?.examList.length - 1) + '개'}
                                                </span>
                                            </strong>
                                            {data &&
                                                data?.examList?.length <= 1 &&
                                                getSuffix(data?.examList[0]?.examNm ?? '')}
                                        </p>
                                        <p className="txtCenter">
                                            <strong>
                                                {data?.publishType === 1 && '아래 클래스에 '}온라인
                                                평가로 발행했습니다.
                                            </strong>
                                        </p>
                                        {/* {data?.publishType === 2 && (
                                            <p className="info1 gray cont-mt txtCenter thin summary_txt">
                                                {isMultiPublish
                                                    ? '자료와 클래스를 골라 평가를 바로 시작할 수 있습니다.'
                                                    : '평가를 바로 시작하시겠습니까?'}
                                            </p>
                                        )} */}
                                    </div>
                                    {data?.pdPublishStDt && data?.pdPublishEdDt && (
                                        <>
                                            <div className="tag-box cont-mt tag-data-box">
                                                <div className="tag-item">
                                                    <span className="tag-txt tag-data-txt">
                                                        응시 기간
                                                    </span>
                                                    <span className="tag-txt tag-data">
                                                        {data?.pdPublishStDt}
                                                    </span>{' '}
                                                    ~{' '}
                                                    <span className="tag-txt tag-data">
                                                        {data?.pdPublishEdDt}
                                                    </span>
                                                </div>
                                            </div>
                                            {data.publishType === 2 && (
                                                <p className="info1 gray2 cont-mt txtCenter summary_txt">
                                                    학생들은 아래 URL/QR을 통해 평가에 응시할 수
                                                    있습니다.
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* 클래스 */}
                {data?.publishType === 1 && isSingePubMultiClass && (
                    <>
                        <div className="form-row mt0">
                            <div className="form-td">
                                <div className="cont_wrap">
                                    <div className="cont_wrap_inner">
                                        <div className="info_wrap">
                                            <p className="info1 arrowpd cont-mt">
                                                <i></i>클래스명 클릭 시 해당 클래스로 이동합니다.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="cont_wrap_inner search-form tag_wrap complite_tag">
                                        <div className="tag-box class-grop">
                                            {data?.examList[0]?.publishList?.map((e, i) => (
                                                <div
                                                    key={`class-tag-item-${i}`}
                                                    className="tag-item"
                                                    onClick={() => onStartEval()}
                                                >
                                                    <span className="tag-txt class">
                                                        {findClaName(e.claId)}
                                                        {/* <span className="user_count">
                                                            {e.publishStdtCnt === e.claStdtCnt &&
                                                            e.publishStdtCnt !== 0
                                                                ? '반 전체'
                                                                : e.publishStdtCnt + '명'}
                                                        </span> */}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {/* 클래스 */}
                {data?.publishType === 1 && !isSingePubMultiClass && (
                    <div className="form-row">
                        <div className="form-td">
                            <div className="cont_wrap">
                                {isMultiPublish && (
                                    <div className="cont_wrap_inner">
                                        <div className="info_wrap">
                                            <p className="info1 arrowpd">
                                                <i></i>평가를 시작할 자료와 클래스를 선택하세요.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="cont_wrap_inner">
                                    {isMultiPublish ? (
                                        <div className="class_group group">
                                            <div
                                                className="dropdown-wrap"
                                                style={{ marginTop: '8px' }}
                                            >
                                                <div className="dropdown-box">
                                                    <div
                                                        className={`dropdown-btn ${selectExamOpen && 'isOpen'}`}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={setSelectExamOpen}
                                                        >
                                                            {data.examList[selectExamIndex]?.examNm}
                                                        </button>
                                                    </div>
                                                    <div
                                                        className={`dropdown-content ${selectExamOpen && 'isOpen'}`}
                                                    >
                                                        <div className="option-type-default">
                                                            {data.examList.map((item, idx) => (
                                                                <div
                                                                    key={`publish-option-class-${idx}`}
                                                                    className={`option ${selectExam === item.examId && 'isCurrent'}`}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectExamOpen();
                                                                            setSelectExam(
                                                                                item.examId
                                                                            );
                                                                        }}
                                                                    >
                                                                        {item.examNm}
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className="dropdown-wrap"
                                                style={{ marginTop: '8px' }}
                                            >
                                                <div className="dropdown-box">
                                                    <div
                                                        className={`dropdown-btn ${selectClassOpen && 'isOpen'}`}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={setSelectClassOpen}
                                                        >
                                                            {findClaName(
                                                                data.examList[selectExamIndex]
                                                                    ?.publishList[selectClassIndex]
                                                                    ?.claId
                                                            )}
                                                        </button>
                                                    </div>
                                                    <div
                                                        className={`dropdown-content ${selectClassOpen && 'isOpen'}`}
                                                    >
                                                        <div className="option-type-default">
                                                            {data?.examList[
                                                                selectExamIndex
                                                            ]?.publishList?.map((item, idx) => (
                                                                <div
                                                                    key={`publish-option-class-${idx}`}
                                                                    className={`option ${selectClass === item.publishId && 'isCurrent'}`}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectClassOpen();
                                                                            setSelectClass(
                                                                                item.publishId
                                                                            );
                                                                        }}
                                                                    >
                                                                        {findClaName(item.claId) ??
                                                                            '-'}
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="borderBox class_group one">
                                            <p>
                                                {findClaName(
                                                    data?.examList[0].publishList[0].claId
                                                ) ?? '-'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* URL */}
                {data?.publishType === 2 && (
                    <>
                        <div className="form-row">
                            <div className="form-td url_link_form">
                                <div className="cont_wrap">
                                    {isMultiPublish && (
                                        <div className="cont_wrap_inner">
                                            <div className="info_wrap">
                                                <p className="info1 arrowpd">
                                                    <i></i>공유할 평가를 선택하세요.
                                                </p>
                                            </div>
                                            <div
                                                className="dropdown-wrap"
                                                style={{ marginTop: '8px' }}
                                            >
                                                <div className="dropdown-box">
                                                    <div
                                                        className={`dropdown-btn ${selectExamOpen && 'isOpen'}`}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={setSelectExamOpen}
                                                        >
                                                            {data.examList[selectExamIndex]?.examNm}
                                                        </button>
                                                    </div>
                                                    <div
                                                        className={`dropdown-content ${selectExamOpen && 'isOpen'}`}
                                                    >
                                                        <div className="option-type-default">
                                                            {data.examList.map((item, idx) => (
                                                                <div
                                                                    key={`publish-option-class-${idx}`}
                                                                    className={`option ${selectExam === item.examId && 'isCurrent'}`}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectExamOpen();
                                                                            setSelectExam(
                                                                                item.examId
                                                                            );
                                                                        }}
                                                                    >
                                                                        {item.examNm}
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="cont_wrap_inner">
                                        <div className="form-period">
                                            <div className="text-box type-url">
                                                <label>
                                                    <input
                                                        type="text"
                                                        className=""
                                                        value={shortUrl ?? shareUrl}
                                                        title="url 복사"
                                                        disabled
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-default url"
                                                        onClick={onCopy}
                                                    >
                                                        URL복사
                                                    </button>
                                                </label>
                                                <button
                                                    type="button"
                                                    className="btn btn-default line save"
                                                    onClick={onQrOpen}
                                                >
                                                    QR저장
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="form-row mt0">
                            <div className="form-td">
                                <div className="cont_wrap">
                                    <div className="cont_wrap_inner">
                                        <div className="bt_wrap cont-mt">
                                            <button
                                                className="btn btn-default icon share vivaclass"
                                                onClick={onShareViva}
                                            >
                                                <i></i>비바클래스에 공유하기
                                            </button>
                                            <button
                                                className="btn btn-default icon share google"
                                                onClick={onShareGoogle}
                                            >
                                                <i></i>구글클래스룸에 공유하기
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <div className="bt_wrap cont-mt2">
                    {data?.publishType === 1 && !isSingePubMultiClass && (
                        <Dialog.Close asChild>
                            <button className="btn btn-default line_gray1 pop_cancle type2">
                                취소
                            </button>
                        </Dialog.Close>
                    )}
                    <button className="btn btn-default blue pop_start" onClick={onConfirm}>
                        {data?.publishType === 1 && !isSingePubMultiClass
                            ? !!data?.pdPublishEdDt || !!data?.pdPublishStDt
                                ? '평가 미리보기'
                                : '평가 시작하기'
                            : '확인'}
                    </button>
                </div>
            </div>
        </div>
    );
};
